// material-ui
import { Dialog, Grid, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import { cloneDeep } from 'lodash-es';
import moment from 'moment-timezone';
import React from 'react';

// api
import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import {
  EAllowStatus,
  IAdserverAlias,
  ResourceActiveDisabled,
  ResourceActiveEnabled,
} from '~/src/dash/api/console-api';
import {
  cloneAdserverOrder,
  createAdserverOrder,
  getAdserverOrder,
  getUniqueOrderAliases,
  IAdserverOrder,
  IAdserverOrderFrequencyCap,
  updateAdserverOrder,
} from '~/src/dash/api/order';
import {
  Day,
  findDaypartingIntervals,
  handleAddDaypartingPeriod,
  handleDaypartingDateChange,
  handleDaypartingDayClick,
  handleDeleteDaypartingPeriod,
  IDaypartingInterval,
  parseDaypartingIntervals,
  scheduleStrings,
} from '~/src/dash/components/dayparting-selector';
import { ModalDialogCustomAcknowledgementView } from '~/src/dash/components/modal-dialog-custom-acknowledgement-view';
import { ModalDialogIncludeCreativeView } from '~/src/dash/components/modal-dialog-include-creative-view';
// components
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { ModelDialogActiveView } from '~/src/dash/components/model-dialog-active-view';
// other
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  parseLocation,
  RouteOrderList,
  RouteOrderSettingsExisting,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { obfuscateOrderName } from '~/src/dash/utils/obfuscate';
import { getOrderPacingTypeOption } from '~/src/dash/utils/pacing-type-options';
// views
import { LoadingView } from '~/src/dash/views/loading-view';
import { OrderSettingsForm } from '~/src/dash/views/orders/order-settings-form';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  // active modal params
  activeModalActive: boolean;
  activeModalID: string | number;
  activeModalName: string;
  acknowledgementLoading: boolean;
  acknowledgementModalActive: boolean;
  cloneLoading: boolean;
  daypartingIntervals: IDaypartingInterval[];
  includeCreatives: boolean;
  includeCreativesModalActive: boolean;
  isDaypartingActive: boolean;
  loaded: boolean; // ok to render the network settings view
  openPacingDialog: boolean;
  order: IAdserverOrder;
  pacingDialogItems: IPacingDialogItem[];
  refresh: number;
  snackbarError: boolean;
  snackbarMessage: string;
  uniqueOrderAliases: IAdserverAlias[];
  updating: boolean;
}

export interface IPacingDialogItem extends IAdserverCampaign {
  tableData: { [checked: string]: boolean };
}

export const OrderSettingsView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      activeModalActive: false,
      activeModalID: 0,
      activeModalName: '',
      alias: '',
      acknowledgementLoading: false,
      acknowledgementModalActive: false,
      cloneLoading: false,
      daypartingIntervals: [
        {
          ActiveDays: {
            Sunday: false,
            Monday: false,
            Tuesday: false,
            Wednesday: false,
            Thursday: false,
            Friday: false,
            Saturday: false,
          },
          StartTime: moment().startOf('day'),
          EndTime: moment().startOf('day').add(12, 'hours'),
          Error: '',
        },
      ] as IDaypartingInterval[],
      includeCreatives: false,
      includeCreativesModalActive: this.props.location.pathname.indexOf(`clone`) > -1,
      isDaypartingActive: false,
      loaded: false,
      openPacingDialog: false,
      order: {
        ID: 0,
        Active: ResourceActiveEnabled,
        AdserverOrderFrequencyCaps: [] as IAdserverOrderFrequencyCap[],
        AdvertiserID: 0,
        Alias: '',
        Dayparting: '',
        DaypartingIsAllowed: EAllowStatus.ALLOW,
        Labels: '',
        LineItems: [] as IAdserverCampaign[],
        Name: 'New Order',
        PacingType: 4,
        PublisherID: 0,
        PurchaseOrder: '',
        TraffickerID: 0,
      } as IAdserverOrder,
      pacingDialogItems: [] as IPacingDialogItem[],
      refresh: 1,
      snackbarError: false,
      snackbarMessage: '',
      uniqueOrderAliases: [] as IAdserverAlias[],
      updating: false,
    };

    componentDidMount() {
      this.getOrder();
      this.handleGetUniqueOrderAliases();
    }

    getOrder = (): void => {
      // based on the url path, this could be a new or edit
      const { session, user } = this.props;
      const loc = parseLocation(this.props.location.pathname);
      const orderID = loc.order;
      const isClone = this.props.location.pathname.indexOf(`clone`) > -1;
      if (orderID) {
        getAdserverOrder(orderID, session.publisher.ID)
          .then((order: IAdserverOrder) => {
            // if it is a clone, remove the line items as we don't clone the line item <=> order association
            // the orderID is set to 0 on the backend for a clone request, so we don't need to do it here
            if (isClone) {
              order.LineItems = [] as IAdserverCampaign[];
            }

            let daypartingObj;
            try {
              daypartingObj = JSON.parse(order.Dayparting);
            } catch (e) {
              daypartingObj = {
                Enabled: false,
                Schedule: cloneDeep(scheduleStrings),
              };
            }
            this.setState({
              order,
              activeModalID: order.ID,
              activeModalName: obfuscateOrderName(order, this.props.user.user.Username, order.Name),
              daypartingIntervals: findDaypartingIntervals(cloneDeep(daypartingObj.Schedule)),
              isDaypartingActive: daypartingObj.Enabled,
              loaded: true,
            });
          })
          .catch(() => {
            this.setState(
              {
                snackbarMessage:
                  "You don't have access to this resource. Please create a new one or select one from the list.",
                snackbarError: true,
              },
              () => {
                setTimeout(() => {
                  navigate(
                    RouteOrderList,
                    extractRouteParams({
                      session,
                      user,
                    }),
                  );
                }, 3000);
              },
            );
          });
        // this is a new order
      } else {
        this.setState({
          loaded: true,
        });
      }
    };

    validateOrder(order: IAdserverOrder): string[] {
      const errorMsgs = [] as string[];
      if (order.Name.length === 0) {
        errorMsgs.push(`Name cannot be empty`);
      }
      if (order.Name === 'New Order') {
        errorMsgs.push(`Choose a custom name`);
      }
      if (order.AdvertiserID === 0) {
        errorMsgs.push(`Order must have an Advertiser`);
      }
      if (order.TraffickerID === 0) {
        errorMsgs.push(`Order must have a Trafficker`);
      }
      if (order.PurchaseOrder.length > 250) {
        errorMsgs.push(`Purchase Order cannot be longer than 250 characters`);
      }
      if (order.Labels.length > 250) {
        errorMsgs.push(`Notes cannot be longer than 250 characters`);
      }
      return errorMsgs;
    }

    // -------- HANDLER FUNCTIONS -------- //
    handleCreateDaypartingPeriod = (): void => {
      const daypartingIntervals = handleAddDaypartingPeriod(this.state.daypartingIntervals);
      this.setState({
        daypartingIntervals,
      });
    };

    handleDeleteDaypartingPeriod = (idx: number): void => {
      const daypartingIntervals = handleDeleteDaypartingPeriod(idx, this.state.daypartingIntervals);
      this.setState({
        daypartingIntervals,
      });
    };

    handleDaypartingDateChange = (index: number, time: string, dateType: 'start' | 'end'): void => {
      if (!time) {
        return;
      }

      const DaypartingIntervals = handleDaypartingDateChange(
        index,
        time,
        dateType,
        this.state.daypartingIntervals,
      );
      this.setState({
        daypartingIntervals: DaypartingIntervals,
      });
    };

    handleDaypartingDayClick = (day: Day, idx: number): void => {
      const daypartingIntervals = handleDaypartingDayClick(
        day,
        idx,
        this.state.daypartingIntervals,
      );
      this.setState({
        daypartingIntervals,
      });
    };

    handleChangeDayparting = (
      { target }: React.ChangeEvent<HTMLInputElement>,
      updatedOrder?: IAdserverOrder,
    ): void => {
      const { daypartingIntervals, isDaypartingActive } = this.state;
      if (target.name === 'DaypartingActiveSwitch') {
        this.setState(
          {
            isDaypartingActive: !isDaypartingActive,
          },
          () => {
            if (daypartingIntervals.length === 0) {
              this.handleCreateDaypartingPeriod();
            }
          },
        );
      } else if (target.name === 'DaypartingIsAllowed' && updatedOrder !== undefined) {
        this.handleChange(updatedOrder);
      }
    };

    handleClose = () => {
      navigate(
        RouteOrderList,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    // the form will emit a new order on change and re-render
    handleChange = (order: IAdserverOrder) => {
      if (this.state.updating) {
        return;
      }
      this.setState({
        order,
      });
    };

    handleChangePacingDialogItems = (rows: IPacingDialogItem[]) => {
      const { pacingDialogItems } = this.state;
      const clonedPacingDialogItems = cloneDeep(pacingDialogItems);
      for (const row of rows) {
        for (let idx = 0; idx < clonedPacingDialogItems.length; idx++) {
          if (row.ID === clonedPacingDialogItems[idx].ID && idx !== 0) {
            clonedPacingDialogItems[idx] = row;
          }
        }
      }

      if (clonedPacingDialogItems.length > 0) {
        clonedPacingDialogItems[0].tableData.checked = true;
      }

      this.setState({
        pacingDialogItems: clonedPacingDialogItems,
      });
    };

    handleChangeAlias = (list: IAdserverAlias[]): void => {
      this.setState({
        order: {
          ...this.state.order,
          Alias: list.length > 0 ? list[0].Name : '',
        },
      });
    };

    handleGetUniqueOrderAliases = (): void => {
      getUniqueOrderAliases(this.props.session.publisher.ID).then((aliases) => {
        this.setState({
          uniqueOrderAliases: aliases,
        });
      });
    };

    handleSubmit = (order: IAdserverOrder) => {
      const { session } = this.props;
      const { daypartingIntervals, isDaypartingActive } = this.state;

      if (this.state.updating) {
        return;
      }
      const errorMsgs = this.validateOrder(order);
      if (errorMsgs.length) {
        this.setState({
          snackbarMessage: errorMsgs.join(', '),
          snackbarError: true,
        });
        return;
      }
      this.setState(
        {
          updating: true,
        },
        () => {
          // remove children => these are not saved into DB since they are pointers
          order.Advertiser = undefined;
          order.Agency = undefined;
          order.Dayparting = parseDaypartingIntervals(
            daypartingIntervals,
            isDaypartingActive,
            session.publisher.Timezone,
            true,
          );
          order.Trafficker = undefined;
          order.Salesperson = undefined;
          if (this.state.order.ID === 0) {
            order.LineItems = [];
            createAdserverOrder(order, this.props.session.publisher.ID)
              .then((newOrder: IAdserverOrder) => {
                this.setState(
                  {
                    order: newOrder,
                    refresh: this.state.refresh + 1,
                    snackbarError: false,
                    snackbarMessage: 'Successfully created order',
                    updating: false,
                  },
                  this.getOrder,
                );
                navigate(RouteOrderSettingsExisting, extractRouteParams(this.props), {
                  order: newOrder.ID.toString(),
                });
              })
              .catch((err) => {
                console.error(err);
                this.setState({
                  snackbarMessage: `Error while creating order. ${err}`,
                  snackbarError: true,
                  updating: false,
                });
              });
          } else {
            const isClone = this.props.location.pathname.indexOf('clone') > -1;
            if (isClone) {
              order.LineItems = [];
              this.setState(
                {
                  cloneLoading: true,
                },
                () => {
                  cloneAdserverOrder(
                    order,
                    this.props.session.publisher.ID,
                    this.state.includeCreatives,
                  )
                    .then((newOrder: IAdserverOrder) => {
                      let daypartingObj;
                      try {
                        daypartingObj = JSON.parse(newOrder.Dayparting);
                      } catch (e) {
                        daypartingObj = {
                          Enabled: false,
                          Schedule: cloneDeep(scheduleStrings),
                        };
                      }
                      this.setState(
                        {
                          cloneLoading: false,
                          daypartingIntervals: findDaypartingIntervals(
                            cloneDeep(daypartingObj.Schedule),
                          ),
                          isDaypartingActive: daypartingObj.Enabled,
                          order: newOrder,
                          refresh: this.state.refresh + 1,
                          snackbarError: false,
                          snackbarMessage: 'Successfully created order',
                          updating: false,
                        },
                        () => {
                          navigate(RouteOrderList, extractRouteParams(this.props));
                        },
                      );
                    })
                    .catch((err) => {
                      console.error(err);
                      this.setState({
                        snackbarError: true,
                        snackbarMessage: `Error while cloning order. ${err}`,
                        updating: false,
                      });
                    });
                },
              );
            } else {
              // from this, we will always make the line item children the same active status as the order
              updateAdserverOrder(order, this.props.session.publisher.ID, true, true)
                .then((newOrder: IAdserverOrder) => {
                  let daypartingObj;
                  try {
                    daypartingObj = JSON.parse(newOrder.Dayparting);
                  } catch (e) {
                    daypartingObj = {
                      Enabled: false,
                      Schedule: cloneDeep(scheduleStrings),
                    };
                  }
                  this.setState(
                    {
                      daypartingIntervals: findDaypartingIntervals(
                        cloneDeep(daypartingObj.Schedule),
                      ),
                      isDaypartingActive: daypartingObj.Enabled,
                      order: newOrder,
                      refresh: this.state.refresh + 1,
                      snackbarError: false,
                      snackbarMessage: 'Successfully updated order',
                      updating: false,
                    },
                    () => {
                      this.getOrder();
                      window.location.href =
                        '/#' +
                        getRoutePath(RouteOrderSettingsExisting, extractRouteParams(this.props), {
                          order: newOrder.ID.toString(),
                        }) +
                        `&refresh=${this.state.refresh}`;
                    },
                  );
                })
                .catch((err) => {
                  console.error(err);
                  this.setState({
                    snackbarMessage: `Error while updating order. ${err}`,
                    snackbarError: true,
                    updating: false,
                  });
                });
            }
          }
        },
      );
    };

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    handleOpenActiveDialog = (): void => {
      this.setState({
        activeModalActive: true,
      });
    };

    handleCloseActiveDialog = (): void => {
      this.setState({
        activeModalActive: false,
      });
    };

    handleCloseIncludeCreativesDialog = (): void => {
      this.setState({
        includeCreativesModalActive: false,
        acknowledgementModalActive: true,
      });
    };

    handleCloseAcknowledgementDialog = (): void => {
      this.setState(
        {
          acknowledgementModalActive: false,
        },
        () => {
          this.handleSubmit(this.state.order);
        },
      );
    };

    handleClosePacingDialog = (saveOrder: boolean) => {
      const { order } = this.state;
      this.setState(
        {
          openPacingDialog: false,
        },
        () => {
          if (saveOrder) {
            this.handleSubmit(order);
          }
        },
      );
    };

    handleCopyPacing = () => {
      const { order, pacingDialogItems } = this.state;
      const selectedLineItemIDs = new Set<string>();
      for (let idx = 0; idx < pacingDialogItems.length; idx++) {
        if (pacingDialogItems[idx].tableData.checked && idx !== 0) {
          selectedLineItemIDs.add(pacingDialogItems[idx].ID);
        }
      }

      const clonedLineItems = cloneDeep(order.LineItems);
      for (let idx = 0; idx < clonedLineItems.length; idx++) {
        if (selectedLineItemIDs.has(clonedLineItems[idx].ID)) {
          // @ts-ignore
          clonedLineItems[idx].PacingType = order.PacingType.toString();
        }
      }

      const clonedOrder = cloneDeep(order);
      clonedOrder.LineItems = clonedLineItems;

      this.setState(
        {
          openPacingDialog: false,
          order: clonedOrder,
        },
        () => this.handleSubmit(clonedOrder),
      );
    };

    handlePacingDialog = (openPacingDialog: boolean): void => {
      const { order } = this.state;

      const pacingTypeMismatch = order.LineItems.some(
        (lineItem) => lineItem.PacingType.toString() !== order.PacingType.toString(),
      );
      if (pacingTypeMismatch) {
        const pacingDialogItems = [] as any[];
        pacingDialogItems.push({
          ID: `Order ${order.ID}`,
          Name: order.Name,
          PacingType: order.PacingType,
          tableData: {
            checked: true,
          },
        });
        for (let idx = 0; idx < order.LineItems.length; idx++) {
          const lineItemPacingType = order.LineItems[idx].PacingType.toString();
          const orderPacingType = order.PacingType.toString();
          if (lineItemPacingType !== orderPacingType) {
            pacingDialogItems.push({
              ID: order.LineItems[idx].ID,
              Name: order.LineItems[idx].Name,
              PacingType: lineItemPacingType,
              tableData: {
                checked: true,
              },
            });
          }
        }

        this.setState({
          openPacingDialog: true,
          pacingDialogItems,
        });
        return;
      }

      this.setState({
        openPacingDialog,
      });
    };

    // switches active on order
    handleActiveItem = (orderID: number) => {
      if (this.state.updating) {
        return;
      }
      const { session } = this.props;
      const { order } = this.state;
      const activeStatus =
        order.Active === ResourceActiveDisabled ? ResourceActiveEnabled : ResourceActiveDisabled;

      // create a new order with the ID and the updated status
      const updatedOrder = {
        ID: orderID,
        Active: activeStatus,
      } as IAdserverOrder;
      this.setState(
        {
          updating: true,
        },
        () => {
          // since we are only updating the active field, this is a partial update rather than a full update
          // since this is passed into the modal, we will always update all child line items to match active status
          updateAdserverOrder(updatedOrder, session.publisher.ID, false, true)
            .then(() => {
              // after updating the order, get the update order and update status
              this.setState(
                {
                  snackbarMessage: 'Successfully saved order',
                  snackbarError: false,
                  updating: false,
                },
                this.getOrder,
              );
            })
            .catch(() => {
              this.setState({
                snackbarMessage: 'Failed to save order',
                snackbarError: true,
                updating: false,
              });
            });
          // the active modal expects a promise to be returned
        },
      );
      return new Promise((resolve) => resolve(undefined));
    };

    renderPacingDialog = () => {
      return (
        <Grid item style={{ marginLeft: -10 }}>
          <Grid container direction='row' justify='space-between'>
            <Typography style={{ fontSize: 24, fontWeight: 500, marginLeft: 22 }}>
              Pacing Mismatch
            </Typography>
            <Grid item xs={1} style={{ textAlign: 'right' }}>
              <CloseIcon
                style={{ cursor: 'pointer', color: '#707070' }}
                onClick={() => this.handleClosePacingDialog(false)}
              />
            </Grid>
          </Grid>
          <Grid
            container
            direction='row'
            style={{ cursor: 'pointer', marginBottom: 10, marginTop: 10 }}
            onClick={this.handleCopyPacing}
          >
            <SaveIcon style={{ color: '5D66FA', marginLeft: 22 }} />
            <Typography style={{ color: '5D66FA', fontWeight: 500, marginLeft: 10 }}>
              Copy Pacing To Selected Line Items
            </Typography>
          </Grid>
          <Grid
            container
            direction='row'
            style={{ cursor: 'pointer', marginBottom: 10, marginTop: 10 }}
            onClick={() => this.handleClosePacingDialog(true)}
          >
            <SaveIcon style={{ color: '5D66FA', marginLeft: 22 }} />
            <Typography style={{ color: '5D66FA', fontWeight: 500, marginLeft: 10 }}>
              No Changes And Save Order
            </Typography>
          </Grid>
        </Grid>
      );
    };

    render() {
      const {
        activeModalActive,
        activeModalID,
        activeModalName,
        acknowledgementLoading,
        acknowledgementModalActive,
        cloneLoading,
        daypartingIntervals,
        includeCreativesModalActive,
        isDaypartingActive,
        loaded,
        openPacingDialog,
        order,
        pacingDialogItems,
        snackbarMessage,
        snackbarError,
      } = this.state;
      if (loaded) {
        const pacingTypeColumn = {
          title: 'Pacing Type',
          field: 'PacingType',
          searchable: true,
          render: (rowData: IPacingDialogItem) => {
            const pacingTypeOption = getOrderPacingTypeOption(rowData.PacingType);
            return pacingTypeOption ? (
              <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                {pacingTypeOption.text}
              </Typography>
            ) : null;
          },
        };

        return (
          <Grid>
            <OrderSettingsForm
              daypartingIntervals={daypartingIntervals}
              handleChangeDayparting={this.handleChangeDayparting}
              handleChangeAlias={this.handleChangeAlias}
              handleCreateDaypartingPeriod={this.handleCreateDaypartingPeriod}
              handleDaypartingDateChange={this.handleDaypartingDateChange}
              handleDaypartingDayClick={this.handleDaypartingDayClick}
              handleDeleteDaypartingPeriod={this.handleDeleteDaypartingPeriod}
              isDaypartingActive={isDaypartingActive}
              onClose={this.handleClose}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
              order={order}
              openActiveDialog={this.handleOpenActiveDialog}
              openPacingDialog={() => this.handlePacingDialog(true)}
              getOrder={this.getOrder}
              uniqueOrderAliases={this.state.uniqueOrderAliases}
            />
            <ModalDialogCustomAcknowledgementView
              active={acknowledgementModalActive}
              acknowledgement={'Order Clone Successful, Please Review Line Item Flight Dates'}
              acknowledgementLoading={acknowledgementLoading}
              onClose={this.handleCloseAcknowledgementDialog}
            />
            <ModalDialogIncludeCreativeView
              active={includeCreativesModalActive}
              activeModal={(includeCreatives: boolean) => {
                this.setState({ includeCreatives });
              }}
              cloneLoading={cloneLoading}
              order={order}
              onClose={this.handleCloseIncludeCreativesDialog}
            />
            <ModelDialogActiveView
              active={activeModalActive}
              ID={activeModalID}
              name={activeModalName}
              activeModel={(id: number) => this.handleActiveItem(id)}
              onDelete={this.handleCloseActiveDialog}
              onError={this.handleCloseActiveDialog}
              onClose={this.handleCloseActiveDialog}
              currentlyActive={order.Active === ResourceActiveEnabled}
            />
            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={openPacingDialog}
              onClose={() => this.handleClosePacingDialog(false)}
            >
              <ModalSelectTable
                customColumns={[pacingTypeColumn]}
                enableMultiSelect={true}
                enableSearchBarLeft={true}
                handleSelectionChange={this.handleChangePacingDialogItems}
                interactive={false}
                items={pacingDialogItems}
                renderCustomToolbar={this.renderPacingDialog}
              />
            </Dialog>
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Grid>
        );
      } else {
        return <LoadingView active={true} text='Loading the order settings' />;
      }
    }
  },
);
